<template>
  <div
    ref="previewContainer"
    class="form-title-preview"
    :class="{ 'is-hover': isHovered && !isSelected && !disabled, 'is-selected': isSelected && !disabled, 'is-disabled': disabled }"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
    @click="handleSelect"
  >
    <div class="form-title-wrapper">
      <div v-if="isTitleEditing" class="form-title-edit-box">
        <input
          ref="titleInput"
          v-model="localTitle"
          type="text"
          class="form-title-edit-input"
          @keyup.enter="finishEditingTitle"
          @keyup.esc="cancelEditingTitle"
        />
        <TextButton
          @click.stop="finishEditingTitle"
        >
          <template #prefix>
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M6.75012 12.1275L3.62262 9L2.55762 10.0575L6.75012 14.25L15.7501 5.25L14.6926 4.1925L6.75012 12.1275Z" fill="#1F1F1F"/>
            </svg>
          </template>
          Save
        </TextButton>
      </div>
      <h2 v-else class="form-title">{{ title }}</h2>
      <!-- Original: 
      <ActionButton
        v-if="!isTitleEditing && !disabled"
        text="Edit"
        default-color="#374957"
        hover-text-color="#1E4670"
        pressed-text-color="#1E4670"
        hover-bg-color="rgba(30, 70, 112, 0.08)"
        pressed-bg-color="rgba(30, 70, 112, 0.12)"
        @click.stop="startEditingTitle"
      >
        <template #prefix>
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M18.8969 3.16575C19.0892 3.45687 19.0571 3.85248 18.8008 4.10876L9.60845 13.3011C9.51422 13.3954 9.39659 13.4628 9.26766 13.4965L5.43923 14.4965C5.31437 14.5291 5.18566 14.5284 5.06473 14.4976C4.93616 14.4649 4.81639 14.3982 4.71937 14.3011C4.5311 14.1129 4.45676 13.8389 4.52405 13.5813L5.52405 9.75284C5.55351 9.64003 5.60881 9.52861 5.68529 9.44121L14.9117 0.21967C14.9925 0.138906 15.0898 0.0784602 15.1957 0.0416313C15.2742 0.0143243 15.3574 0 15.4421 0C15.641 0 15.8318 0.0790175 15.9724 0.21967L18.8008 3.0481C18.8374 3.08471 18.8695 3.12416 18.8969 3.16575ZM17.2098 3.57843L15.4421 1.81066L6.92391 10.3288L6.29893 12.7216L8.69169 12.0966L17.2098 3.57843Z" fill="#374957"/>
            <path d="M17.0834 14.9103C17.3568 12.5727 17.4438 10.2188 17.3444 7.87079C17.3397 7.75837 17.3819 7.64898 17.4615 7.56942L18.4448 6.58609C18.5657 6.46519 18.7723 6.54194 18.7836 6.71254C18.9685 9.50219 18.8984 12.3045 18.5733 15.0846C18.3367 17.1071 16.7124 18.6921 14.7003 18.917C11.2338 19.3044 7.65042 19.3044 4.18385 18.917C2.17179 18.6921 0.547457 17.1071 0.310903 15.0846C-0.103634 11.5403 -0.103635 7.95973 0.310903 4.41543C0.547456 2.3929 2.17179 0.807889 4.18385 0.583012C6.8136 0.289099 9.51054 0.218149 12.1727 0.370161C12.3443 0.379958 12.4225 0.587575 12.301 0.709093L11.3084 1.70165C11.2298 1.78034 11.1218 1.82261 11.0106 1.81885C8.78419 1.74376 6.54263 1.82872 4.35046 2.07373C3.02035 2.22239 1.95486 3.272 1.80075 4.58968C1.39975 8.01821 1.39975 11.4818 1.80075 14.9103C1.95486 16.228 3.02035 17.2776 4.35046 17.4263C7.70631 17.8013 11.1779 17.8013 14.5337 17.4263C15.8638 17.2776 16.9293 16.228 17.0834 14.9103Z" fill="#374957"/>
          </svg>
        </template>
      </ActionButton>
      -->
      <FoldableButton
        v-if="!isTitleEditing && !disabled"
        text="Edit"
        built_in_theme="default"
        @click.stop="startEditingTitle"
      >
        <template #prefix>
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M18.8969 3.16575C19.0892 3.45687 19.0571 3.85248 18.8008 4.10876L9.60845 13.3011C9.51422 13.3954 9.39659 13.4628 9.26766 13.4965L5.43923 14.4965C5.31437 14.5291 5.18566 14.5284 5.06473 14.4976C4.93616 14.4649 4.81639 14.3982 4.71937 14.3011C4.5311 14.1129 4.45676 13.8389 4.52405 13.5813L5.52405 9.75284C5.55351 9.64003 5.60881 9.52861 5.68529 9.44121L14.9117 0.21967C14.9925 0.138906 15.0898 0.0784602 15.1957 0.0416313C15.2742 0.0143243 15.3574 0 15.4421 0C15.641 0 15.8318 0.0790175 15.9724 0.21967L18.8008 3.0481C18.8374 3.08471 18.8695 3.12416 18.8969 3.16575ZM17.2098 3.57843L15.4421 1.81066L6.92391 10.3288L6.29893 12.7216L8.69169 12.0966L17.2098 3.57843Z" fill="#374957"/>
            <path d="M17.0834 14.9103C17.3568 12.5727 17.4438 10.2188 17.3444 7.87079C17.3397 7.75837 17.3819 7.64898 17.4615 7.56942L18.4448 6.58609C18.5657 6.46519 18.7723 6.54194 18.7836 6.71254C18.9685 9.50219 18.8984 12.3045 18.5733 15.0846C18.3367 17.1071 16.7124 18.6921 14.7003 18.917C11.2338 19.3044 7.65042 19.3044 4.18385 18.917C2.17179 18.6921 0.547457 17.1071 0.310903 15.0846C-0.103634 11.5403 -0.103635 7.95973 0.310903 4.41543C0.547456 2.3929 2.17179 0.807889 4.18385 0.583012C6.8136 0.289099 9.51054 0.218149 12.1727 0.370161C12.3443 0.379958 12.4225 0.587575 12.301 0.709093L11.3084 1.70165C11.2298 1.78034 11.1218 1.82261 11.0106 1.81885C8.78419 1.74376 6.54263 1.82872 4.35046 2.07373C3.02035 2.22239 1.95486 3.272 1.80075 4.58968C1.39975 8.01821 1.39975 11.4818 1.80075 14.9103C1.95486 16.228 3.02035 17.2776 4.35046 17.4263C7.70631 17.8013 11.1779 17.8013 14.5337 17.4263C15.8638 17.2776 16.9293 16.228 17.0834 14.9103Z" fill="#374957"/>
          </svg>
        </template>
      </FoldableButton>
    </div>
  </div>
</template>

<script>
// Original: import ActionButton from '@/views/commonComponents/ActionButton.vue';
// Replaced with globally registered FoldableButton from @vibrant-wellness/va-responsive-components-library
import TextButton from '@/views/commonComponents/TextButton.vue';

import selectableMixin from './mixins/selectableMixin';

export default {
  name: 'FormTitlePreview',
  components: {
    // ActionButton, // replaced by globally registered FoldableButton
    TextButton
  },
  mixins: [selectableMixin],
  props: {
    selectedId: {
      type: String,
      default: ''
    },
    title: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:title', 'element-deselected', 'element-selected'],
  data() {
    return {
      isTitleEditing: false,
      localTitle: ''
    };
  },
  watch: {
    selectedId(newVal) {
      if (newVal === 'form-title') {
        if (this.isSelected !== true) {
          this.isSelected = true;
        }
        //auto scroll to this element
        this.$refs.previewContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        if (this.isSelected === true) {
          this.isSelected = false;
        }
      }
    },
    title: {
      immediate: true,
      handler(newVal) {
        this.localTitle = newVal;
      }
    },
    isSelected(newVal) {
      if (newVal) {
        this.$emit('element-selected', 'form-title');
      } else {
        if (this.isTitleEditing) {
          this.finishEditingTitle();
        }
        this.$emit('element-deselected', 'form-title');
      }
    }
  },
  methods: {
    handleMouseEnter() {
      if (!this.disabled) {
        this.isHovered = true;
      }
    },
    handleMouseLeave() {
      this.isHovered = false;
    },
    handleSelect() {
      if (this.disabled) {
        return;
      }
      if (!this.isSelected) {
        this.isSelected = true;
      }
    },
    startEditingTitle() {
      if (this.disabled) {
        return;
      }
      this.isSelected = true;
      this.isTitleEditing = true;
      this.$nextTick(() => {
        this.$refs.titleInput?.focus();
        this.$refs.titleInput?.select();
      });
    },
    finishEditingTitle() {
      this.isTitleEditing = false;
      this.$emit('update:title', this.localTitle);
    },
    cancelEditingTitle() {
      this.isTitleEditing = false;
      this.localTitle = this.title;
    }
  }
};
</script>

<style scoped>
.form-title-preview {
  padding: 0;
  cursor: pointer;
  transition: all 0.2s ease;
}
.form-title-preview.is-hovered {
  background-color: rgba(0, 72, 121, 0.08);
}
.form-title-preview.is-disabled {
  cursor: not-allowed;
}

.form-title-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
}

.form-title {
  overflow: hidden;
  color: var(--TrueBlack, #333);
  text-overflow: ellipsis;

  /* EHR_TitleL_22_Web */
  font-family: 'Avenir', sans-serif;
  font-size: 22px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px; /* 109.091% */ 
}

.form-title-edit-box {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  border-radius: 4px;
  border: 1px solid #C4C4C4;  
  padding: 2px 8px;
}

.form-title-edit-input {
  flex: 1;
  /* EHR_TitleL_22_Web */
  font-family: 'Avenir', sans-serif;
  font-size: 22px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px; /* 109.091% */ 
  color: #333333;
  border-radius: 4px;
  outline: none;
}

:deep(.text-button) {
  padding: 1px 6px;
}
</style>
